import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * TaxAdvisorModal - Read-only modal for the Tax Advisor report.
 *
 * Displays the advisory narrative (income_trajectory_note, projection_vs_actual_note,
 * recommendations, analysis_narrative, business_card_note) with confidence/staleness
 * chips, loading state, and a refresh button.
 *
 * Props:
 *   open        - boolean controlling dialog visibility
 *   onClose     - callback to close the dialog
 *   report      - advisorReport object from getTaxAdvisorReport API
 *   loading     - boolean for loading state
 *   error       - error message string
 *   onRefresh   - callback to refresh the advisor report
 *   onGenerate  - callback to generate the first report (same as onRefresh)
 */
function TaxAdvisorModal({ open, onClose, report, loading, error, onRefresh, onGenerate }) {
  const advisory = report?.advisory;
  const hasReport = !!advisory;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: 600, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          Tax Advisor
        </Typography>
        {advisory?.confidence && (
          <Chip
            size="small"
            color={advisory.confidence === 'high' ? 'success' : 'warning'}
            label={advisory.confidence}
          />
        )}
        {report?.is_stale && (
          <Chip label="Stale" color="warning" size="small" variant="outlined" />
        )}
        {report?.as_of && (
          <Chip label={`As of ${report.as_of}`} size="small" variant="outlined" />
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent dividers sx={{ flexGrow: 1, overflow: 'auto' }}>
        {/* Staleness warning */}
        {report?.is_stale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Snapshot is stale ({report.stale_reason || 'unknown reason'}). Advisory may be based on outdated data.
          </Alert>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generating advisory...
            </Typography>
          </Box>
        )}

        {/* No report yet — show generate button */}
        {!loading && !hasReport && !error && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No advisor report available yet.
            </Typography>
            <Button variant="contained" onClick={onGenerate || onRefresh}>
              Generate Report
            </Button>
          </Box>
        )}

        {/* Advisory content */}
        {!loading && hasReport && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
            {/* Income trajectory */}
            {advisory.income_trajectory_note && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {advisory.income_trajectory_note}
              </Typography>
            )}

            {/* Projection vs actual */}
            {advisory.projection_vs_actual_note && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {advisory.projection_vs_actual_note}
              </Typography>
            )}

            {/* Recommendations */}
            {advisory.recommendations?.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>Recommendations</Typography>
                <List dense sx={{ pt: 0 }}>
                  {advisory.recommendations.map((rec, idx) => (
                    <ListItem key={`advisor-rec-${idx}`} sx={{ py: 0.25 }}>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Business card note */}
            {advisory.business_card_note && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Business card note: {advisory.business_card_note}
              </Typography>
            )}

            {/* Narrative */}
            {advisory.analysis_narrative && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {advisory.analysis_narrative}
                </Typography>
              </>
            )}
          </Paper>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {report?.generated_at && (
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            Generated {report.generated_at}
          </Typography>
        )}
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="outlined"
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Advisor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TaxAdvisorModal;
